import React from 'react';
import { Link } from 'react-router-dom';
import { Package, User as UserIcon, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';

const Account = () => {
  const { user } = useAuth();
  const orders = JSON.parse(localStorage.getItem('orders') || '[]');
  const userOrders = orders.filter(order => order.shippingInfo.email === user?.email);

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold mb-8 text-slate-800">My Account</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Info */}
          <div className="lg:col-span-1">
            <Card className="border-2">
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <UserIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Name</p>
                      <p className="font-semibold text-slate-800">{user?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-3 rounded-full">
                      <Package className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Email</p>
                      <p className="font-semibold text-slate-800">{user?.email}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order History */}
          <div className="lg:col-span-2">
            <Card className="border-2">
              <CardHeader>
                <CardTitle>Order History</CardTitle>
              </CardHeader>
              <CardContent>
                {userOrders.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600 mb-4">No orders yet</p>
                    <Link to="/products">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                        Start Shopping
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {userOrders.map(order => (
                      <Card key={order.orderId} className="border">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <p className="font-semibold text-slate-800">Order {order.orderId}</p>
                              <p className="text-sm text-slate-600">
                                {new Date(order.date).toLocaleDateString()}
                              </p>
                              {order.trackingNumber && (
                                <p className="text-xs text-blue-600 mt-1">
                                  Tracking: {order.trackingNumber}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-blue-600">${order.total.toFixed(2)}</p>
                              <span className={`inline-block px-3 py-1 text-xs rounded-full mt-1 ${
                                order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                order.status === 'shipped' ? 'bg-indigo-100 text-indigo-700' :
                                order.status === 'processing' ? 'bg-purple-100 text-purple-700' :
                                order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Confirmed'}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {order.items.slice(0, 2).map(item => (
                              <div key={item.id} className="text-sm text-slate-600">
                                {item.name} x {item.quantity}
                              </div>
                            ))}
                            {order.items.length > 2 && (
                              <p className="text-sm text-slate-500">
                                +{order.items.length - 2} more items
                              </p>
                            )}
                          </div>
                          {order.statusNotes && (
                            <div className="mt-3 p-2 bg-blue-50 rounded text-sm text-slate-700">
                              <strong>Note:</strong> {order.statusNotes}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;